import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import Colors from '@components/shared/Colors';
import { P } from '@components/shared/Typography';
import { DEFAULT_KLAVIYO_LIST_ID } from '@constants';
import { validEmail } from '@utils/regex';
import useNewsletter from '@utils/useNewsletter';
import Spinner from './Spinner';

interface Props {
  location?: string;
  listId?: string;
  style?: string;
  buttonTextKey?: string;
  onSuccess?: () => void;
}

const NewsletterForm: React.FC<Props> = ({
  location = '',
  listId = DEFAULT_KLAVIYO_LIST_ID,
  style = 'inline',
  buttonTextKey = 'components.sign-up',
  onSuccess = null
}) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const { addToNewsletter, newsletterLoading, newsletterSuccessful } = useNewsletter(location, listId);

  useEffect(() => {
    if (newsletterSuccessful) {
      setName('');
      setEmail('');
      if (onSuccess) {
        onSuccess();
      }
    }
  }, [newsletterSuccessful]);

  const handleAddToNewsletter = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      addToNewsletter(undefined, email, name);
    },
    [name, email]
  );

  const renderButtonContent = () => {
    if (newsletterLoading) {
      return <Spinner />;
    }

    if (newsletterSuccessful) {
      return <P $mods={[22]}>✓</P>;
    }

    return <P $mods={[14]}>{t(buttonTextKey).toUpperCase()}</P>;
  };

  const renderButton = () => {
    const buttonDisabled = name.length <= 1 || email.length < 1 || !validEmail.test(email.toLowerCase());
    return (
      <Button type="submit" disabled={buttonDisabled} data-testid="newsletter-form-submit">
        {renderButtonContent()}
      </Button>
    );
  };

  return (
    <Container>
      <form onSubmit={handleAddToNewsletter}>
        <Input
          placeholder={t('components.email-address')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{ opacity: newsletterSuccessful ? 0.5 : 1.0 }}
          autoCorrect="off"
          autoCapitalize="none"
        />
        <InputContainer>
          <Input
            placeholder={t('components.name')}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={{ marginBottom: 10, opacity: newsletterSuccessful ? 0.5 : 1.0 }}
          />
          {style === 'inline' ? renderButton() : null}
        </InputContainer>
        {style === 'popup' ? renderButton() : null}
      </form>
    </Container>
  );
};

export default NewsletterForm;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: row;
`;

const Input = styled.input`
  border: none;
  padding-left: 16px;
  height: 45px;
  font-size: 14px;
  font-family: 'BrownStd';
  background: ${Colors.cream};
  border: 2px solid ${Colors.paleGold};
  color: ${Colors.forestGreen};
  width: 100%;
  border-radius: 2px;
  margin-bottom: 10px;

  &:focus {
    outline: none;
  }

  & + button {
    margin-left: -4px;
  }
`;

const Button = styled.button`
  background-color: ${Colors.paleGold};
  height: 45px;
  width: auto;
  padding: 0 20px;
  border: none;
  white-space: nowrap;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-family: 'BrownStd';
  font-weight: 700;
  letter-spacing: 1px;
  border-radius: 2px;
  font-family: BrownStd;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.7;
  }

  &:disabled {
    opacity: 0.5;
  }
`;
